import { action, computed, observable } from 'mobx';
import {
    ForceStrongAuthReason,
    IsWeakAuthenticatingReason
} from '~/components/ClientExperience/OneFlow/stores/flow-view.ui.store';
import { isPublicFlowRoute } from '~/utils/public-flows.utils';
import { isViewerMode } from '~/utils/viewer-mode-flow.utils';
import { CXRootStore } from '~/components/ClientExperience/stores/cx-root.store';
import { isPublicTemplateViewMode } from '~/utils/public-template-view-mode-flow.utils';
import { isUserLessMode } from '~/utils/user-less-mode-flow.utils';
import { devEnvAttentionLog } from '~/utils/debug.utils';
import { loadUser } from '~/utils/user.utils';

const LIMITED_AUTH_STORAGE_KEY = 'HONEYBOOK_REACT_WEAK_AUTH';
const VIEWER_AUTH_STORAGE_KEY = 'HONEYBOOK_REACT_VIEWER_AUTH';

export interface IWeakLoginProps {
    userEmail?: string;
    userId?: string;
    isRealChargeableUser?: boolean;
}

export function getLimitedAuthStorageKey(flowId: string) {
    if (!flowId) {
        return null;
    }

    const key = isViewerMode()
        ? VIEWER_AUTH_STORAGE_KEY
        : LIMITED_AUTH_STORAGE_KEY;

    return `${key}_${flowId}`;
}

export class CXFlowSessionStore {
    rootStore: CXRootStore;

    @observable userEmail: string;
    @observable userId: string;
    @observable isRealChargeableUser: boolean;
    @observable smsConsentShown: boolean;

    @observable weakAuthenticating: IsWeakAuthenticatingReason;
    @observable forceStrongAuth: ForceStrongAuthReason;

    contextClientId?: string; // when performing actions on behalf of clients

    constructor(rootStore: CXRootStore) {
        this.rootStore = rootStore;
    }

    @computed
    get userWeakAuthKey() {
        if (!this.userEmail && !this.userId) {
            return null;
        }

        if (this.userEmail) {
            return 'email';
        }

        return '_id';
    }

    @computed
    get userWeakAuthData() {
        if (!this.userEmail && !this.userId && !this.isRealChargeableUser) {
            return null;
        }

        const userData = {
            _id: this.userId,
            email: this.userEmail,
            is_real_chargeable_user: this.isRealChargeableUser,
            full_name: null,
            api_version: null,
            authentication_token: null,
            email_signatures: []
        };

        // Initialize stores with account and company data
        const user = loadUser(userData, this.rootStore);

        return user;
    }

    @computed
    get weakAuthUser() {
        return !!this.userWeakAuthData;
    }

    @computed
    get isAuthenticating() {
        return !!this.weakAuthenticating;
    }

    @computed
    get weakAuthenticated() {
        return !!this.userWeakAuthData;
    }

    @computed
    get shouldWeakAuthenticate() {
        const { flowStore } = this.rootStore;

        return (
            !this.forceStrongAuth &&
            flowStore.minimalFlow &&
            (!flowStore.minimalFlow.require_authentication ||
                !!flowStore.minimalFlow?.improved_smaf_auth_on)
        );
    }

    @computed
    get authenticationHashRequired() {
        const weakAuthenticationMissing =
            this.shouldWeakAuthenticate &&
            !this.weakAuthenticated &&
            !this.weakAuthenticating;

        return (
            (weakAuthenticationMissing || !!isViewerMode()) &&
            !isPublicTemplateViewMode()
        );
    }

    @computed
    get isAuthorized() {
        const { sessionStore } = this.rootStore;

        // no class OOP override because sessionStore is stateful.
        if (isPublicFlowRoute() || isUserLessMode()) {
            return true;
        }

        const isWeakAuthorized =
            this.shouldWeakAuthenticate &&
            this.weakAuthenticated &&
            !this.weakAuthenticating;

        return isWeakAuthorized || sessionStore.authenticated;
    }

    @computed
    get limitedAuthStorageKey() {
        const {
            flowViewUiStore: { flowId }
        } = this.rootStore;

        return getLimitedAuthStorageKey(flowId);
    }

    @action
    setUserEmail(userEmail: string) {
        this.userEmail = userEmail;
    }

    @action
    setRealChargeableUser(isRealChargeableUser: boolean) {
        this.isRealChargeableUser = isRealChargeableUser;
    }

    @action
    setSMSConsentShown(smsConsentShown: boolean) {
        this.smsConsentShown = smsConsentShown;
    }

    @action
    setUserId(userId: string) {
        this.userId = userId;
    }

    @action.bound
    setWeakAuthenticating(state: IsWeakAuthenticatingReason) {
        this.weakAuthenticating = state;
    }

    @action.bound
    setForceStrongAuth(forceStrongAuth: ForceStrongAuthReason) {
        this.forceStrongAuth = forceStrongAuth;
    }

    @computed
    get isVendorLogin() {
        const {
            sessionStore,
            flowViewUiStore: { selectedFlow }
        } = this.rootStore;

        return (
            selectedFlow?.isInstance &&
            sessionStore.authenticated &&
            sessionStore.sessionUserType === 'vendor'
        );
    }

    @action.bound
    setContextClient(clientId: string) {
        const {
            configStore: {
                config: {
                    hb_api_headers: { vendor_login }
                }
            }
        } = this.rootStore;

        if (!this.isVendorLogin) {
            devEnvAttentionLog(
                'Context client should not be set unless the vendor is logged in'
            );
            return;
        }

        if (this.isVendorLogin) {
            this.rootStore.apiService.addHeader({ [vendor_login]: clientId });
        }

        this.contextClientId = clientId;
    }
}
